import { VIRTUAL_WIDTH } from "../../config";
import { drawSceneBackground, fillRoundedRect, strokeRoundedRect, truncateToWidth } from "../../utils/draw";
import type { Button } from "../systems/Buttons";
import { backButton } from "../systems/Buttons";
import type { RaceOnline, RaceState } from "../systems/Races";
import { MenuScene } from "./MenuScene";
import { PlayScene } from "./PlayScene";
import type { GameContext, Scene } from "./Scene";

const FIND_BTN = { x: VIRTUAL_WIDTH / 2 - 90, y: 300, w: 180, h: 44 };
const CANCEL_BTN = { x: VIRTUAL_WIDTH / 2 - 70, y: 360, w: 140, h: 36 };

/** A battle whose live race this scene plays out, or null for a quick race. */
export interface BattleRaceSource {
  id: string;
  bet: number;
  opponent: string;
}

export class RaceScene implements Scene {
  private mode: "idle" | "searching" = "idle";
  private raceId: string | null = null;
  private pollAccum = 0;
  private polling = false;
  private statusMsg = "";
  // Live "who's racing now" panel, refreshed every few seconds. Shown to
  // everyone, including logged-out visitors.
  private online: RaceOnline | null = null;
  private onlineAccum = 0;
  private onlineLoading = false;
  // Guards async matchmaking callbacks from acting after the scene has left.
  private entered = false;

  constructor(
    private readonly ctxMgr: GameContext,
    private readonly battle: BattleRaceSource | null = null,
    /** Set for a friendly (no-stake) race against a specific friend by name. */
    private readonly friendOpponent: string | null = null,
  ) {}

  /** A battle or friend race has a fixed opponent (auto-matchmakes, no Find button). */
  private get targeted(): boolean {
    return this.battle !== null || this.friendOpponent !== null;
  }

  enter(): void {
    this.entered = true;
    this.rebuild();
    void this.refreshOnline();
    // A targeted race has a fixed opponent — start matchmaking immediately.
    if (this.targeted) void this.startSearch();
  }

  exit(): void {
    this.entered = false;
    this.ctxMgr.buttons.clear();
    // Drop our waiting room if we leave mid-search.
    if (this.mode === "searching" && this.raceId) {
      const { playerName, authToken } = this.ctxMgr.settings;
      void this.ctxMgr.races.cancel(playerName, authToken, this.raceId);
    }
  }

  update(dt: number): void {
    // Refresh the live racer count regardless of mode (works logged-out too).
    this.onlineAccum += dt;
    if (this.onlineAccum >= 5 && !this.onlineLoading) {
      this.onlineAccum = 0;
      void this.refreshOnline();
    }

    if (this.mode !== "searching" || !this.raceId || this.polling) return;
    this.pollAccum += dt;
    if (this.pollAccum < 0.8) return;
    this.pollAccum = 0;
    this.polling = true;
    const { playerName, authToken } = this.ctxMgr.settings;
    void this.ctxMgr.races.poll(playerName, authToken, this.raceId).then((st) => {
      this.polling = false;
      if (!this.entered) return;
      if (st && st.status === "racing") this.launch(st);
    });
  }

  private async refreshOnline(): Promise<void> {
    this.onlineLoading = true;
    const data = await this.ctxMgr.races.online();
    this.onlineLoading = false;
    if (this.entered && data) this.online = data;
  }

  private async startSearch(): Promise<void> {
    if (!this.ctxMgr.settings.isLoggedIn()) {
      window.alert("Log in to race other players.");
      return;
    }
    this.mode = "searching";
    const targetName = this.battle?.opponent ?? this.friendOpponent;
    this.statusMsg = targetName ? `Waiting for ${targetName} to race…` : "Searching for an opponent…";
    this.rebuild();
    const { playerName, authToken } = this.ctxMgr.settings;
    const st = this.battle
      ? await this.ctxMgr.battles.startRace(playerName, authToken, this.battle.id)
      : this.friendOpponent
        ? await this.ctxMgr.races.friend(playerName, authToken, this.friendOpponent)
        : await this.ctxMgr.races.quick(playerName, authToken);
    if (!this.entered) return; // left the race screen while matchmaking
    if (!st) {
      this.mode = "idle";
      this.statusMsg = this.battle
        ? "You've already raced this battle, or it's no longer active."
        : this.friendOpponent
          ? "Couldn't start the race (are you still friends?)."
          : "Couldn't reach the race server.";
      this.rebuild();
      return;
    }
    if (st.status === "racing") {
      this.launch(st);
      return;
    }
    this.raceId = st.id; // waiting — update() polls until someone joins
  }

  private launch(st: RaceState): void {
    if (st.startAt === null) return;
    this.ctxMgr.goTo(
      new PlayScene(this.ctxMgr, null, {
        id: st.id,
        seed: st.seed,
        startAt: st.startAt,
        opponent: st.opponent ?? this.battle?.opponent ?? this.friendOpponent ?? "opponent",
        ...(this.battle ? { battleId: this.battle.id, stake: this.battle.bet } : {}),
      }),
    );
  }

  private cancelSearch(): void {
    if (this.raceId) {
      const { playerName, authToken } = this.ctxMgr.settings;
      void this.ctxMgr.races.cancel(playerName, authToken, this.raceId);
    }
    this.raceId = null;
    this.mode = "idle";
    this.statusMsg = "";
    // A targeted race has nothing else to do here — return to the menu.
    if (this.targeted) {
      this.ctxMgr.goTo(new MenuScene(this.ctxMgr));
      return;
    }
    this.rebuild();
  }

  private rebuild(): void {
    const buttons: Button[] = [
      backButton(() => {
        this.cancelSearch();
        this.ctxMgr.goTo(new MenuScene(this.ctxMgr));
      }),
    ];

    if (this.mode === "idle" && !this.targeted) {
      buttons.push({
        ...FIND_BTN,
        draw: (ctx, hit) => drawBigButton(ctx, "Find a Race", FIND_BTN, hit),
        onPress: () => void this.startSearch(),
      });
    } else if (this.mode === "searching") {
      buttons.push({
        ...CANCEL_BTN,
        draw: (ctx, hit) => drawBigButton(ctx, "Cancel", CANCEL_BTN, hit),
        onPress: () => this.cancelSearch(),
      });
    }

    this.ctxMgr.buttons.set(buttons);
  }

  render(ctx: CanvasRenderingContext2D, _alpha: number): void {
    drawSceneBackground(ctx);

    ctx.fillStyle = "#f5e9c8";
    ctx.font = "bold 24px system-ui, sans-serif";
    ctx.textAlign = "center";
    ctx.fillText(this.battle ? "BATTLE RACE" : "RACE", VIRTUAL_WIDTH / 2, 80);

    ctx.font = "13px system-ui, sans-serif";
    const subMaxW = VIRTUAL_WIDTH - 24;
    if (this.battle) {
      ctx.fillText(
        truncateToWidth(ctx, `vs ${this.battle.opponent} · ${this.battle.bet} flies on the line`, subMaxW),
        VIRTUAL_WIDTH / 2,
        120,
      );
      ctx.fillText("Higher score takes the pot.", VIRTUAL_WIDTH / 2, 142);
    } else if (this.friendOpponent) {
      ctx.fillText(
        truncateToWidth(ctx, `vs ${this.friendOpponent} · friendly race`, subMaxW),
        VIRTUAL_WIDTH / 2,
        120,
      );
      ctx.fillText("You'll see each other as a ghost.", VIRTUAL_WIDTH / 2, 142);
    } else {
      ctx.fillText("Race a live opponent on identical pipes.", VIRTUAL_WIDTH / 2, 120);
      ctx.fillText("You'll see each other as a ghost.", VIRTUAL_WIDTH / 2, 142);
    }

    // Drawn for everyone — logged in or not — so all visitors see the activity.
    this.renderOnline(ctx);

    if (!this.ctxMgr.settings.isLoggedIn()) {
      ctx.fillText("Log in to race.", VIRTUAL_WIDTH / 2, 220);
      return;
    }

    if (this.mode === "searching") {
      const dots = ".".repeat(1 + (Math.floor(performance.now() / 400) % 3));
      ctx.fillStyle = "#f0e0a8";
      ctx.font = "16px system-ui, sans-serif";
      ctx.fillText(`${this.statusMsg}${dots}`, VIRTUAL_WIDTH / 2, 250);
      ctx.font = "11px system-ui, sans-serif";
      ctx.fillStyle = "#f5e9c8";
      const hint = this.friendOpponent
        ? `(${this.friendOpponent} must tap Join in their friends list)`
        : this.battle
          ? `(${this.battle.opponent} must open this battle and race too)`
          : "(a friend must hit Find a Race too)";
      ctx.fillText(hint, VIRTUAL_WIDTH / 2, 272);
    } else if (this.statusMsg) {
      ctx.fillStyle = "#ff8a8a";
      ctx.font = "13px system-ui, sans-serif";
      ctx.fillText(this.statusMsg, VIRTUAL_WIDTH / 2, 250);
    }
  }

  /** "N players racing now" + a short list of their names. Visible to all. */
  private renderOnline(ctx: CanvasRenderingContext2D): void {
    const count = this.online?.count ?? 0;
    const names = this.online?.players ?? [];
    const shown = names.slice(0, 6);
    const hasMore = names.length > shown.length;

    const panelW = 220;
    const x = VIRTUAL_WIDTH / 2 - panelW / 2;
    const y = 430;
    const rows = count === 0 ? 0 : shown.length + (hasMore ? 1 : 0);
    const panelH = 34 + rows * 16;

    ctx.fillStyle = "#222838";
    fillRoundedRect(ctx, x, y, panelW, panelH, 8);
    ctx.strokeStyle = "#3a4256";
    ctx.lineWidth = 1;
    strokeRoundedRect(ctx, x + 0.5, y + 0.5, panelW - 1, panelH - 1, 8);

    ctx.textAlign = "center";
    ctx.fillStyle = count === 0 ? "#8a93a8" : "#9fe6a0";
    ctx.font = "bold 14px system-ui, sans-serif";
    const label =
      count === 0
        ? "No one racing right now"
        : count === 1
          ? "1 player racing now"
          : `${count} players racing now`;
    ctx.fillText(label, VIRTUAL_WIDTH / 2, y + 22);

    ctx.font = "12px system-ui, sans-serif";
    ctx.fillStyle = "#d8e0f0";
    shown.forEach((n, i) => ctx.fillText(n, VIRTUAL_WIDTH / 2, y + 40 + i * 16));
    if (hasMore) {
      ctx.fillStyle = "#8a93a8";
      ctx.fillText(`+${names.length - shown.length} more`, VIRTUAL_WIDTH / 2, y + 40 + shown.length * 16);
    }
  }
}

function drawBigButton(
  ctx: CanvasRenderingContext2D,
  label: string,
  r: { x: number; y: number; w: number; h: number },
  hit: boolean,
): void {
  ctx.fillStyle = hit ? "#7a5e2a" : "#f5e9c8";
  fillRoundedRect(ctx, r.x, r.y, r.w, r.h, 10);
  ctx.strokeStyle = "#7a5e2a";
  ctx.lineWidth = 2;
  strokeRoundedRect(ctx, r.x + 0.5, r.y + 0.5, r.w - 1, r.h - 1, 10);
  ctx.fillStyle = hit ? "#f5e9c8" : "#7a5e2a";
  ctx.font = "bold 17px system-ui, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(label, r.x + r.w / 2, r.y + r.h / 2);
  ctx.textBaseline = "alphabetic";
}

